import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Shield, Clock, Star, ArrowRight, Phone, Mail, MapPin, CheckCircle2, Sofa, Bed, Armchair, Layers, Square, HelpCircle, ChevronRight, MessageCircle, Send, Navigation, Lock, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import api, { fileUrl } from "@/lib/api";

const services = [
    { icon: Sofa, title: "Sofás", desc: "Tecido, camurça, microfibra", price: "desde 30€ / lugar" },
    { icon: Bed, title: "Colchões", desc: "Higienização e desinfeção", price: "desde 35€" },
    { icon: Armchair, title: "Cadeiras", desc: "Sala, escritório, jantar", price: "desde 10€ / un." },
    { icon: Layers, title: "Carpetes", desc: "Lavagem profunda", price: "desde 8€ / m²" },
    { icon: Armchair, title: "Poltronas", desc: "Poltronas e cadeirões", price: "desde 25€ / un." },
    { icon: Square, title: "Alcatifas", desc: "Divisão a divisão", price: "desde 6€ / m²" },
];

const TESTIMONIALS_FALLBACK = [];

export default function Landing() {
    const [gallery, setGallery] = useState([]);
    const [testimonials, setTestimonials] = useState(TESTIMONIALS_FALLBACK);

    useEffect(() => {
        api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => setGallery([]));
        api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => setTestimonials([]));
    }, []);

    // Build before/after pairs from gallery
    const pairs = {};
    const singles = [];
    for (const g of gallery) {
        if (g.pair_id && g.pair_role) {
            pairs[g.pair_id] = pairs[g.pair_id] || {};
            pairs[g.pair_id][g.pair_role] = g;
        } else {
            singles.push(g);
        }
    }
    const validPairs = Object.entries(pairs).filter(([, p]) => p.before && p.after);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white">
            {/* Top strip */}
            <div className="ab-top-strip">
                Higienização profissional de estofos · Orçamento online em minutos · Porto e Arredores
            </div>

            {/* Header */}
            <header className="ab-glass-header sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
                        <Logo variant="full" size={54} />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-10">
                        <a href="#servicos" className="ab-nav-link">Serviços</a>
                        <a href="#trabalhos" className="ab-nav-link">Trabalhos</a>
                        <a href="#como-funciona" className="ab-nav-link">Como funciona</a>
                        <a href="#contactos" className="ab-nav-link">Contactos</a>
                    </nav>
                    <div className="flex items-center gap-4 shrink-0">
                        <Link
                            to="/admin/login"
                            aria-label="Iniciar sessão no painel"
                            title="Iniciar sessão"
                            data-testid="admin-link"
                            className="w-10 h-10 flex items-center justify-center border border-white/15 rounded-full text-white/70 hover:text-[#C9A961] hover:border-[#C9A961] transition-colors"
                        >
                            <Lock className="w-4 h-4" />
                        </Link>
                        <Link to="/orcamento">
                            <Button data-testid="calc-quote-btn-header" className="ab-btn-primary rounded-none px-6 py-2 text-sm uppercase tracking-wider font-semibold">
                                Calcular orçamento
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1686178827149-6d55c72d81df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBzb2ZhJTIwY2xlYW5pbmd8ZW58MHx8fHwxNzg1NTk2Nzc3fDA&ixlib=rb-4.1.0&q=85"
                        alt="Limpeza profissional de estofos"
                        className="w-full h-full object-cover opacity-45"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/40 to-[#0A0A0A]" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 lg:py-44 text-center ab-fade-up">
                    <div className="ab-eyebrow mb-6">Alto Brilho · Estofos</div>
                    <h1 className="ab-headline text-5xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl mx-auto">
                        Higienização <span className="text-[#C9A961]">Profissional</span><br />
                        de Estofos
                    </h1>
                    <p className="mt-8 text-lg lg:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                        Sofás, colchões, cadeiras, carpetes e alcatifas. Devolvemos brilho e frescura ao seu conforto.
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/orcamento">
                            <Button data-testid="calc-quote-btn-hero" size="lg" className="ab-btn-primary rounded-none h-14 px-10 text-sm font-semibold uppercase tracking-widest">
                                Calcular Orçamento
                                <ArrowRight className="w-4 h-4 ml-3" />
                            </Button>
                        </Link>
                        <a href="#servicos">
                            <Button variant="outline" size="lg" className="ab-btn-outline-gold rounded-none h-14 px-10 text-sm font-semibold uppercase tracking-widest">
                                Ver serviços
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Trust bar */}
            <section className="border-y border-[#2A2A2A] bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { icon: Shield, label: "Produtos Certificados" },
                        { icon: Clock, label: "Serviço em 48h" },
                        { icon: Star, label: "4.9 / 5 · +3200 serviços" },
                        { icon: CheckCircle2, label: "Sem compromisso" },
                    ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
                            <item.icon className="w-5 h-5 text-[#C9A961]" />
                            <span className="text-xs md:text-sm font-medium text-white/80 uppercase tracking-wider">{item.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Services */}
            <section id="servicos" className="py-24 lg:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="ab-eyebrow mb-4">Serviços</div>
                        <h2 className="ab-headline text-4xl lg:text-5xl">
                            O que <span className="text-[#C9A961]">limpamos</span> por si
                        </h2>
                        <p className="mt-6 text-white/60 text-lg leading-relaxed">
                            Trabalhamos com equipamento profissional e produtos seguros para toda a família e animais de estimação.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {services.map((s, i) => (
                            <div key={i} className="ab-card p-8" data-testid={`service-tile-${s.title.toLowerCase()}`}>
                                <div className="w-12 h-12 border border-[#C9A961]/30 bg-[#C9A961]/5 flex items-center justify-center rounded-none mb-6">
                                    <s.icon className="w-5 h-5 text-[#C9A961]" />
                                </div>
                                <h3 className="font-heading font-bold text-xl uppercase tracking-wide text-white">{s.title}</h3>
                                <p className="mt-2 text-sm text-white/50">{s.desc}</p>
                                <div className="mt-6 pt-6 border-t border-[#2A2A2A] flex items-center justify-between">
                                    <span className="text-sm font-semibold text-[#C9A961] uppercase tracking-wider">{s.price}</span>
                                    <ChevronRight className="w-4 h-4 text-[#C9A961]" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="como-funciona" className="py-24 lg:py-32 bg-[#111111] border-y border-[#2A2A2A]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="ab-eyebrow mb-4">Como funciona</div>
                        <h2 className="ab-headline text-4xl lg:text-5xl">
                            Do orçamento à casa <span className="text-[#C9A961]">impecável</span>
                        </h2>
                        <p className="mt-6 text-white/60 text-lg">Simples, transparente e sem compromisso.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        {[
                            { n: "01", t: "Preencha em 7 passos", d: "Escolha os serviços, envie fotografias e receba uma estimativa imediata." },
                            { n: "02", t: "Confirmamos e agendamos", d: "A nossa equipa entra em contacto para validar detalhes e agendar o serviço." },
                            { n: "03", t: "Higienização profissional", d: "Rápido, limpo e sem desmontar mobília desnecessariamente." },
                        ].map((s, i) => (
                            <div key={i} className="ab-card p-10 relative">
                                <div className="ab-headline text-6xl text-[#C9A961]/20 absolute right-6 top-6">{s.n}</div>
                                <h3 className="font-heading font-bold text-xl uppercase tracking-wide text-white mt-8 relative">{s.t}</h3>
                                <p className="mt-4 text-white/60 leading-relaxed relative">{s.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-16 text-center">
                        <Link to="/orcamento">
                            <Button className="ab-btn-primary rounded-none h-14 px-10 text-sm uppercase tracking-widest font-semibold" data-testid="calc-quote-btn-how">
                                Começar agora
                                <ArrowRight className="w-4 h-4 ml-3" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Gallery / trabalhos realizados */}
            {(gallery.length > 0 || validPairs.length > 0) && (
                <section id="trabalhos" className="py-24 lg:py-32">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="text-center max-w-2xl mx-auto mb-16">
                            <div className="ab-eyebrow mb-4">Trabalhos realizados</div>
                            <h2 className="ab-headline text-4xl lg:text-5xl">
                                Antes & <span className="text-[#C9A961]">depois</span>
                            </h2>
                            <p className="mt-6 text-white/60 text-lg">
                                {validPairs.length > 0 ? "Arraste o cursor sobre a imagem para comparar." : "Prova real da qualidade dos nossos serviços."}
                            </p>
                        </div>
                        {validPairs.length > 0 && (
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                {validPairs.slice(0, 4).map(([pairId, p]) => (
                                    <BeforeAfterSlider key={pairId} before={p.before} after={p.after} title={p.after.title || p.before.title} />
                                ))}
                            </div>
                        )}
                        {singles.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {singles.slice(0, 8).map((g) => (
                                    <div key={g.id} className="ab-card overflow-hidden aspect-square group relative" data-testid={`gallery-tile-${g.id}`}>
                                        {g.media_type === "video" ? (
                                            <video src={fileUrl(g.storage_path)} className="w-full h-full object-cover" muted loop playsInline onMouseEnter={(e) => e.currentTarget.play()} onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }} />
                                        ) : (
                                            <img src={fileUrl(g.storage_path)} alt={g.title || "Trabalho realizado"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        )}
                                        {(g.title || g.description) && (
                                            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="font-heading font-bold text-white text-sm">{g.title}</div>
                                                {g.description && <div className="text-white/70 text-xs mt-1 line-clamp-2">{g.description}</div>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Testimonials */}
            {testimonials.length > 0 && (
            <section id="testemunhos" className="py-24 lg:py-32 bg-[#111111] border-y border-[#2A2A2A]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="ab-eyebrow mb-4">Testemunhos</div>
                        <h2 className="ab-headline text-4xl lg:text-5xl">
                            O que dizem os nossos <span className="text-[#C9A961]">clientes</span>
                        </h2>
                        <div className="mt-6 inline-flex items-center gap-3">
                            <div className="flex" data-testid="testimonials-avg-stars">
                                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-[#C9A961]" fill="#C9A961" />)}
                            </div>
                            <span className="text-white/70 text-sm">{(testimonials.reduce((s, t) => s + (t.rating || 5), 0) / testimonials.length).toFixed(1)} / 5 · {testimonials.length} testemunho{testimonials.length !== 1 ? "s" : ""}</span>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {testimonials.slice(0, 8).map((t, i) => (
                            <div key={t.id || i} className="ab-card p-8 flex flex-col" data-testid={`testimonial-${i}`}>
                                <div className="flex mb-4">
                                    {[...Array(t.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 text-[#C9A961]" fill="#C9A961" />)}
                                </div>
                                <p className="text-white/80 text-sm leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>
                                <div className="mt-6 pt-6 border-t border-[#2A2A2A] flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex items-center justify-center font-heading font-bold text-[#C9A961]">
                                        {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                    </div>
                                    <div>
                                        <div className="font-heading font-bold text-white text-sm">{t.name}</div>
                                        <div className="text-xs text-white/50">{t.city}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* CTA banner */}
            <section className="py-24 lg:py-32 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxjYXJwZXQlMjBjbGVhbmluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODU1OTY3Nzd8MA&ixlib=rb-4.1.0&q=85"
                        alt=""
                        className="w-full h-full object-cover opacity-25"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/70" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="ab-eyebrow mb-4">Pronto para começar</div>
                    <h3 className="ab-headline text-4xl lg:text-6xl">
                        Dê vida nova aos seus <span className="text-[#C9A961]">estofos</span>
                    </h3>
                    <p className="mt-6 text-white/70 text-lg">Peça o orçamento online. Recebe estimativa em minutos.</p>
                    <div className="mt-10">
                        <Link to="/orcamento">
                            <Button className="ab-btn-primary rounded-none h-14 px-12 text-sm uppercase tracking-widest font-semibold" data-testid="calc-quote-btn-cta">
                                Calcular Orçamento
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact */}
            <section id="contactos" className="py-24 border-t border-[#2A2A2A]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center mb-16">
                        <div className="ab-eyebrow mb-4">Contactos</div>
                        <h2 className="ab-headline text-3xl lg:text-4xl">Fale connosco</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Telefone + WhatsApp */}
                        <div className="ab-card p-8 flex flex-col">
                            <div className="w-11 h-11 border border-[#C9A961]/30 bg-[#C9A961]/5 flex items-center justify-center mb-5">
                                <Phone className="w-5 h-5 text-[#C9A961]" />
                            </div>
                            <div className="text-xs text-white/50 uppercase tracking-widest font-medium">Telefone</div>
                            <a href="tel:+351967318921" className="font-heading font-bold text-white text-lg mt-2 hover:text-[#C9A961] transition-colors" data-testid="contact-phone">
                                +351 967 318 921
                            </a>
                            <a
                                href="https://wa.me/351967318921?text=Ol%C3%A1%21%20Gostaria%20de%20pedir%20um%20or%C3%A7amento."
                                target="_blank"
                                rel="noreferrer"
                                className="mt-auto pt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#25D366] hover:text-[#3ee87c] transition-colors"
                                data-testid="contact-whatsapp"
                            >
                                <MessageCircle className="w-4 h-4" /> Conversar no WhatsApp
                            </a>
                        </div>

                        {/* Email */}
                        <a href="mailto:altobrilho24@gmail.com?subject=Pedido%20de%20informa%C3%A7%C3%A3o" className="block group" data-testid="contact-email">
                            <div className="ab-card p-8 flex flex-col h-full">
                                <div className="w-11 h-11 border border-[#C9A961]/30 bg-[#C9A961]/5 flex items-center justify-center mb-5">
                                    <Mail className="w-5 h-5 text-[#C9A961]" />
                                </div>
                                <div className="text-xs text-white/50 uppercase tracking-widest font-medium">Email</div>
                                <div className="font-heading font-bold text-white text-lg mt-2 group-hover:text-[#C9A961] transition-colors break-all">
                                    altobrilho24@gmail.com
                                </div>
                                <div className="mt-auto pt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#C9A961]">
                                    <Send className="w-4 h-4" /> Enviar email
                                </div>
                            </div>
                        </a>

                        {/* Loja */}
                        <div className="ab-card p-8 flex flex-col" data-testid="contact-store">
                            <div className="w-11 h-11 border border-[#C9A961]/30 bg-[#C9A961]/5 flex items-center justify-center mb-5">
                                <MapPin className="w-5 h-5 text-[#C9A961]" />
                            </div>
                            <div className="text-xs text-white/50 uppercase tracking-widest font-medium">Loja</div>
                            <div className="font-heading font-bold text-white text-lg mt-2 leading-snug">
                                Alto Brilho
                            </div>
                            <div className="text-white/70 text-sm mt-1 leading-snug">
                                Rua de S. Tirso 455<br />
                                4620-848 Meinedo · Lousada
                            </div>
                            <a
                                href="https://www.google.com/maps/place/Alto+Brilho+%7C+Loja+de+Produtos+de+Limpeza+Automotiva+e+Casa/@41.2439325,-8.2588292,18z/data=!3m1!4b1!4m6!3m5!1s0xd2493493a7b26ff:0x68e33d4e3b8567e1!8m2!3d41.2439309!4d-8.257791!16s%2Fg%2F11xsggyy_4"
                                target="_blank"
                                rel="noreferrer"
                                className="mt-auto pt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#C9A961] hover:text-[#E5C989] transition-colors"
                                data-testid="contact-maps"
                            >
                                <Navigation className="w-4 h-4" /> Ver no Google Maps
                            </a>
                        </div>

                        {/* Cobertura */}
                        <div className="ab-card p-8 flex flex-col">
                            <div className="w-11 h-11 border border-[#C9A961]/30 bg-[#C9A961]/5 flex items-center justify-center mb-5">
                                <MapPin className="w-5 h-5 text-[#C9A961]" />
                            </div>
                            <div className="text-xs text-white/50 uppercase tracking-widest font-medium">Cobertura</div>
                            <div className="font-heading font-bold text-white text-lg mt-2">Porto e Arredores</div>
                            <div className="text-white/50 text-sm mt-1">Serviço ao domicílio</div>
                        </div>
                    </div>
                </div>
            </section>

            <WhatsAppFAB />

            <footer className="border-t border-[#2A2A2A] py-12 bg-[#050505]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10 items-center">
                    <div className="flex items-center gap-3">
                        <Logo variant="mark" size={44} />
                        <div>
                            <div className="font-heading font-bold text-white uppercase tracking-widest">Alto Brilho</div>
                            <div className="text-xs text-white/50">Limpeza profissional de estofos</div>
                        </div>
                    </div>
                    <div className="text-center text-xs text-white/50">
                        © {new Date().getFullYear()} Alto Brilho. Todos os direitos reservados.
                    </div>
                    <div className="text-center md:text-right text-xs text-white/50">
                        Feito com cuidado em Portugal.
                    </div>
                </div>
            </footer>
        </div>
    );
}
